import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import './KPICharts.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

function buildMetricSeries(metric) {
  const goalValue = Number.isFinite(metric.goal) ? metric.goal : null;
  const actualValue = Number.isFinite(metric.actual) ? metric.actual : null;
  const color = metric.status === 'behind' ? '#f97316' : metric.status === 'ahead' ? '#10b981' : '#2563eb';

  return {
    labels: ['Goal', 'Actual'],
    datasets: [
      {
        label: metric.label,
        data: [goalValue, actualValue],
        backgroundColor: [
          'rgba(148, 163, 184, 0.5)',
          metric.status === 'behind'
            ? 'rgba(249, 115, 22, 0.72)'
            : metric.status === 'ahead'
              ? 'rgba(16, 185, 129, 0.72)'
              : 'rgba(37, 99, 235, 0.72)'
        ],
        borderColor: [
          'rgba(148, 163, 184, 0.85)',
          color
        ],
        borderWidth: 1,
        borderRadius: 12,
        borderSkipped: false,
        maxBarThickness: 42
      }
    ]
  };
}

function getChartTone(metric) {
  if (metric.status === 'ahead') {
    return 'good';
  }
  if (metric.status === 'behind') {
    return 'warning';
  }
  return 'fair';
}

function getChartBadgeCopy(metric) {
  if (!Number.isFinite(metric.percentToGoal)) {
    return 'Pending';
  }

  if (Number.isFinite(metric.rollingProgress)) {
    if (metric.rollingProgress >= 98) {
      return 'On Pace';
    }
    if (metric.rollingProgress >= 85) {
      return 'Watch';
    }
    return 'Off Pace';
  }

  return `${metric.percentToGoal.toFixed(metric.percentToGoal >= 100 ? 0 : 1)}%`;
}

function KPICharts({ trackerMetrics = [] }) {
  if (!trackerMetrics.length) {
    return <div className="charts-error">No rolling goal and actual data available for this period.</div>;
  }

  const chartMetrics = trackerMetrics.slice(0, 3);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 15
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(148, 163, 184, 0.18)',
          drawBorder: false
        },
        ticks: {
          color: '#64748b',
          font: {
            size: 11
          }
        }
      },
      x: {
        grid: {
          display: false
        },
        ticks: {
          color: '#475569',
          font: {
            size: 11,
            weight: '600'
          }
        }
      }
    }
  };

  return (
    <div className="kpi-charts">
      {chartMetrics.map(metric => (
        <div className={`chart-container ${Number.isFinite(metric.actual) ? '' : 'is-awaiting'}`.trim()} key={metric.kpiKey}>
          <div className="chart-header">
            <div>
              <h3>{metric.label}</h3>
              <p>{Number.isFinite(metric.actual) ? 'Target vs actual' : 'Actual pending'}</p>
            </div>
            <span className={`chart-status-badge ${getChartTone(metric)}`}>
              {getChartBadgeCopy(metric)}
            </span>
          </div>
          <div className="chart-meta-row">
            <span>Monthly target {Number.isFinite(metric.goal) ? metric.goal : 'n/a'}</span>
            <span>MTD actual {Number.isFinite(metric.actual) ? metric.actual : 'n/a'}</span>
          </div>
          <div className="chart-plot-shell">
            <Bar data={buildMetricSeries(metric)} options={chartOptions} />
          </div>
        </div>
      ))}
    </div>
  );
}

export default KPICharts;
